import React, {useEffect, useState} from 'react';
import {ReactComponent as OpenFullScreen} from '../../../../assests/svg/openFullScreen.svg';
import {
  Box,
  CssBaseline,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import BarChartCell from '../../../reports/HostingVolumeReport/BarChartCell';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import ProcessingExceptionsDialog from '../../../pages/welcome-screen-single-client/ProcessingExceptionsDialog';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {t} from 'i18next';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import {getProcessingException} from '../../../../api/EcaManagementService';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

interface Exceptions {
  name: string;
  count: number;
}
interface ExceptionType {
  processingExceptionTypeId: string;
  name: string;
  processingException: Exceptions[];
}

interface ExceptionCount {
  totalDocument: number;
  totalException: number;
  maxCount: number;
}
interface EcaProcessingExceptionsProps {
  loadFileId: number | undefined;
}

type InertDivProps = React.HTMLAttributes<HTMLDivElement> & {
  inert?: string;
};

const hiddenGridProps: InertDivProps = {
  'aria-hidden': true,
  inert: '',
};

const EcaProcessingExceptions: React.FC<EcaProcessingExceptionsProps> = ({
  loadFileId,
}) => {
  const [processingExceptionsDialogOpen, setProcessingExceptionsDialogOpen] =
    useState<boolean>(false);
  const [, setProcessingExceptions] = useState<ExceptionType[]>([]);
  const [, setExceptionCount] = useState<ExceptionCount>();
  const [allExceptionsList, setAllExceptionsList] = useState<Exceptions[]>([]);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [loading, setLoading] = useState(true);
  const {trackGAEvent} = useAnalytics();

  const {projectId, selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );
  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );

  const fetchProcessingExceptions = async (params: string) => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const response = await getProcessingException(params);
      const exceptionCount = response?.data?.data.reduce(
        (acc: ExceptionCount, type: ExceptionType) => {
          type.processingException.forEach((exception: Exceptions) => {
            acc.totalException++;
            acc.totalDocument += exception.count;
            if (exception.count > acc.maxCount) {
              acc.maxCount = exception.count;
            }
          });
          return acc;
        },
        {totalException: 0, totalDocument: 0, maxCount: 0}
      );

      setProcessingExceptions(response?.data?.data);
      const allExceptions = response?.data?.data.reduce(
        (acc: Exceptions[], type: ExceptionType) => {
          return [...acc, ...type.processingException];
        },
        []
      );

      setAllExceptionsList(allExceptions);
      setExceptionCount(exceptionCount);
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    fetchWithParams();
  }, [selectedClient.clientId, projectId, loadFileId]);

  const fetchWithParams = () => {
    let paramString;
    if (loadFileId) {
      paramString = `clientId=${selectedClient.clientId}&projectId=${projectId}&loadFilesId=${loadFileId}`;
    } else {
      paramString = `clientId=${selectedClient.clientId}&projectId=${projectId}`;
    }
    fetchProcessingExceptions(paramString);
  };

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const sortedRows = [...allExceptionsList]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  const maxDocumentCount = Math.max(...sortedRows.map((row) => row.count));
  const rowsWithId = sortedRows.map((row, index) => ({
    ...row,
    id: index,
    name: row.name,
  }));
  const columns = [
    {
      field: 'name',
      headerName: t('common.tableHeaders.name'),
      flex: 1,
      headerClassName: 'no-separator',
      sortable: false,
    },
    {
      field: 'documentsCount',
      headerName: t('common.tableHeaders.documentsCount'),
      flex: 3.5,
      headerClassName: 'no-separator',
      sortable: false,
      renderCell: (params: any) => {
        const rowIndex = params.rowIndex;
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <BarChartCell
              value={params.row.count}
              maxValue={maxDocumentCount}
              name="custodian-value"
              showLabel={true}
              fullWidthBar={true}
              showTooltip={false}
              isFirst={rowIndex === 0}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Grid item xs={6}>
      <div className="doc-column">
        <div className="doc-header">
          <div className="d-flex">
            <Typography className="typo-text subhead4_size">
              {t('ecaHomepageModals.ecaBottom.processingExceptions')} &nbsp;
            </Typography>
            <Typography className="sub-text-count">
              ({allExceptionsList.length})
            </Typography>
          </div>
          <span id="process-exceptions-expand-hint" className="sr-only">
            {t('eca.ecaHomepage.ecaProcessingExceptions')}
          </span>
          <IconButton
            size="small"
            onClick={() => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.ecaHomePage.expandWidget'),
                  type: GA_CLICK_TYPES.ICON,
                  region: GA_REGIONS.WIDGET_BODY,
                  action: t('gaTracking.ecaHomePage.modalLabelwidget', {
                    label: t('gaTracking.ecaHomePage.processingExceptions'),
                  }),
                },
              });
              setProcessingExceptionsDialogOpen(true);
              trackGAEvent({
                eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                subPage: {
                  subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                    label: t('gaTracking.ecaHomePage.processingExceptions'),
                  }),
                  subpage_type: GA_SUBPAGE_TYPE.MODAL,
                },
              });
            }}
            className="expand-button"
            aria-label={`${t('ecaHomepageModals.ecaBottom.processingExceptions')} ${t('buttons.expand')}`}
            aria-describedby='process-exceptions-expand-hint'
          >
            <OpenFullScreen />
          </IconButton>

          {processingExceptionsDialogOpen && (
            <ProcessingExceptionsDialog
              projectId={projectId}
              isOpen={processingExceptionsDialogOpen}
              onClose={() => setProcessingExceptionsDialogOpen(false)}
              loadFileId={loadFileId}
              projectName={projectName}
            />
          )}
        </div>

        {loading ? (
          <Box
            className="loader-center"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <div {...hiddenGridProps}>
            <AurDataGridPro
              rows={rowsWithId}
              getRowId={(row) => row.id}
              columns={columns}
              rowHeight={35}
              checkBoxVisible={false}
              hideFooter
              columnHeaderHeight={0}
              headerFilterHeight={0}
              className="white-table-grid"
              noRowsOverlay={
                isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
              }
              onErrorRefresh={() => fetchWithParams()}
              disableCellHeaderFocus
            />
            </div>
          </ThemeProvider>
        )}
      </div>
    </Grid>
  );
};

export default EcaProcessingExceptions;
